import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Dayjs } from "dayjs";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { Menu } from "client/jspPlatformExperiment";

interface MenusInWeekStateProps {
  menus: Menu[] | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  menuType?: "cycling menu" | "alternate menu";
  isLoading: boolean;
  error: string | null | undefined;
  warnings: string | null;
  selectedMenusByDate: Record<string, number[]>;
}

const initialState: MenusInWeekStateProps = {
  menus: [],
  startDate: null,
  endDate: null,
  menuType: undefined,
  isLoading: false,
  error: null,
  warnings: null,
  selectedMenusByDate: {},
};

/** Return value of week menu data from the Jasper Platform API call */
const fetchMenusInWeek = createAsyncThunk(
  "menusInWeek/fetchMenusInWeek",
  async (
    {
      startDate,
      endDate,
      menuType,
      forSiteId,
    }: {
      startDate: Dayjs;
      endDate: Dayjs;
      menuType?: "cycling menu" | "alternate menu";
      forSiteId?: number | null;
    },
    { signal, rejectWithValue },
  ) => {
    return jspPlatformApi()
      .menu.getMenuDateRangeFromByDateStartToByDateEnd({
        path: {
          date_start: startDate.format("YYYY-MM-DD"),
          date_end: endDate.format("YYYY-MM-DD"),
        },
        // View-only page: skip the expensive server-side tag aggregation.
        query: {
          aggregate_food_tags: false,
          ...(menuType ? { menu_type: menuType } : {}),
          ...(forSiteId != null ? { for_site_id: forSiteId } : {}),
        },
        signal,
      })
      .then((res) => {
        const menus = res.data ?? [];
        const filteredMenus = (Array.isArray(menus) ? menus : []).filter(
          (menu: { name?: string | null }) => !menu.name?.includes("Texture"),
        );
        return {
          menus: filteredMenus,
          startDate,
          endDate,
          menuType,
        };
      })
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      });
  },
);

const toggleSelectedMenusByDate = (
  state: MenusInWeekStateProps,
  action: PayloadAction<{
    date: string;
    menuId: number;
  }>,
): MenusInWeekStateProps => {
  const { date, menuId } = action.payload;
  const prevMenuIds = state.selectedMenusByDate[date] || [];
  return {
    ...state,
    selectedMenusByDate: {
      ...state.selectedMenusByDate,
      [date]: prevMenuIds.includes(menuId)
        ? prevMenuIds.filter((id) => id !== menuId)
        : [...prevMenuIds, menuId],
    },
  };
};

const updateMenusInWeek = (
  state: MenusInWeekStateProps,
  action: PayloadAction<{
    menus: Menu[];
    startDate: Dayjs;
    endDate: Dayjs;
    menuType?: "cycling menu" | "alternate menu";
  } | null>,
): MenusInWeekStateProps => {
  if (action.payload) {
    return {
      ...state,
      menus: action.payload.menus,
      startDate: action.payload.startDate,
      endDate: action.payload.endDate,
      menuType: action.payload.menuType,
      selectedMenusByDate: {},
    };
  }
  return initialState;
};

const updateMenuType = (
  state: MenusInWeekStateProps,
  action: PayloadAction<"cycling menu" | "alternate menu">,
): MenusInWeekStateProps => {
  return {
    ...state,
    menuType: action.payload,
  };
};

export const menusInWeekSlice = createSlice({
  name: "weekMenuViewDetailsSlice",
  initialState,
  reducers: {
    updateMenusInWeek,
    updateMenuType,
    toggleSelectedMenusByDate,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMenusInWeek.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchMenusInWeek.fulfilled, (state, action) => {
      return {
        ...state,
        ...updateMenusInWeek(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchMenusInWeek.rejected, (state, action) => {
      return {
        ...state,
        isLoading: false,
        error: action.payload as string,
      };
    });
  },
});

export const menusInWeekActions = {
  ...menusInWeekSlice.actions,
  fetchMenusInWeek,
};

const weekMenuViewDetailsReducer = menusInWeekSlice.reducer;

export default weekMenuViewDetailsReducer;
